/**
 * Tela inicial do canal (/canal/mercadolivre e /canal/shopee), de cima para baixo:
 *   0. seletor de período [Semana | Mês] (padrão Semana; lembrado por usuário neste navegador);
 *   1. cards de resultado: Faturamento · Pedidos · Ticket médio · Meta × Projeção. Valor de hoje até agora; "vs ontem"
 *      (até a mesma hora) e, conforme o seletor, "vs semana passada" (mesmo dia e hora) ou "vs mês passado" (mesmo dia
 *      do mês e hora). Meta × Projeção: semana = do dia; mês = do mês. Verde ▲ melhora, vermelho ▼ piora;
 *   2. cards de anúncios (uma seção só; vêm da página do canal);
 *   3. [Saúde da integração e da conta] | [Pendências], cada um recolhível (lembrado por usuário);
 *   4. aviso do buy box (Mercado Livre);
 *   5. [Itens que pararam de vender] | [Top itens em ruptura]: 5 linhas à vista, o resto com rolagem na tabela; Exportar
 *      traz a lista completa. Seguem o seletor (períodos comparáveis: o mesmo número de dias até agora).
 *   6. Top 50 mais vendidos (30 ou 7 dias, lembrado por usuário), com Exportar. Com duas lojas ou mais, só a loja escolhida.
 * Os avisos da página (conta expirada, leitura com falha, loja de teste...) e os alertas críticos ficam acima dos cards.
 * API: /home/channel/results e /home/channel/items (channelHome.ts); saúde e pendências: /general/channel.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { api, brl, int } from '../api.ts';
import { ErrorBox, Loading } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { CriticalAlerts, HealthPanel, PendingPanel, useChannelBoard } from './ChannelBoard.tsx';
import { BuyBoxNotice, TopPanel, type Top } from './Overview.tsx';

type Channel = 'mercadolivre' | 'shopee';
export type HomePeriod = 'week' | 'month';
interface Triple { today: number; yesterday: number; other: number }
interface Results {
  now: { today: string; time: string };
  period: HomePeriod;
  dates: { yesterday: string; other: string };
  buybox: { losing: number; byPrice: number; total: number } | null;
  amount: Triple; orders: Triple;
  ticket: { today: number | null; yesterday: number | null; other: number | null };
  goal: { scope: 'day' | 'month'; goal: number | null; projection: number | null; source: string | null; provisional: boolean; perStore: boolean };
}
interface ItemRow { id: string; sku: string; title: string; prevUnits: number; available: number; listing: string; listingStatus: string | null }
interface Items {
  period: HomePeriod;
  range: { curFrom: string; curTo: string; prevFrom: string; prevTo: string };
  stopped: { count: number; items: (ItemRow & { curUnits: number })[] };
  rupture: { count: number; listings: number; items: (ItemRow & { days: number | null })[] };
}

const PERIODS: [HomePeriod, string][] = [['week', 'Semana'], ['month', 'Mês']];
const PREV: Record<HomePeriod, string> = { week: 'semana passada', month: 'mês passado' };
const CUR: Record<HomePeriod, string> = { week: 'semana atual', month: 'mês atual' };
const SHORT_PREV: Record<HomePeriod, string> = { week: 'sem. passada', month: 'mês passado' };
const SHORT_CUR: Record<HomePeriod, string> = { week: 'sem. atual', month: 'mês atual' };
const PREV_IN: Record<HomePeriod, string> = { week: 'na semana passada', month: 'no mês passado' };
const CUR_IN: Record<HomePeriod, string> = { week: 'nesta semana', month: 'neste mês' };
const brl0 = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const dm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

/** Preferência simples por usuário (neste navegador): período e quadros recolhidos. */
function usePref<T extends string>(key: string, def: T, allowed: readonly T[]): [T, (v: T) => void] {
  const me = useMe();
  const k = `berytus.canal.${me.user.id}.${key}`;
  const [v, setV] = useState<T>(() => {
    try { const x = localStorage.getItem(k) as T | null; return x && allowed.includes(x) ? x : def; } catch { return def; }
  });
  const set = (x: T) => { setV(x); try { localStorage.setItem(k, x); } catch { /* sem armazenamento */ } };
  return [v, set];
}

/** Variação: ▲ verde quando melhora, ▼ vermelho quando piora; sem base, só o traço. */
function Delta({ cur, base, label, fmt }: { cur: number | null; base: number | null; label: string; fmt?: (v: number) => string }) {
  if (cur == null || base == null || base === 0) {
    return <span className="delta muted">— {label}{base === 0 ? ' (sem venda na base)' : ''}</span>;
  }
  const pct = (cur / base - 1) * 100;
  const shown = Math.round(pct * 10) / 10;
  const tone = shown > 0 ? 'up' : shown < 0 ? 'down' : 'flat';
  return (
    <span className={`delta ${tone}`} title={fmt ? `${label}: ${fmt(base)}` : undefined}>
      {tone === 'up' ? '▲' : tone === 'down' ? '▼' : '='} {shown > 0 ? '+' : ''}{shown.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% {label}
      {fmt && <span className="muted"> · {fmt(base)}</span>}
    </span>
  );
}

function ResultCards({ r, period }: { r: Results; period: HomePeriod }) {
  const vsOther = `vs ${PREV[period]}`;
  const g = r.goal;
  const goalLabel = g.scope === 'day' ? 'do dia' : 'do mês';
  return (
    <section className="kpis four" aria-label="Resultado de hoje">
      <div className="kpi">
        <span className="k-label">Faturamento</span>
        <span className="k-value">{brl(r.amount.today)}</span>
        <span className="k-note">hoje até as {r.now.time}</span>
        <Delta cur={r.amount.today} base={r.amount.yesterday} label="vs ontem" fmt={brl} />
        <Delta cur={r.amount.today} base={r.amount.other} label={vsOther} fmt={brl} />
      </div>
      <div className="kpi">
        <span className="k-label">Pedidos</span>
        <span className="k-value">{int(r.orders.today)}</span>
        <span className="k-note">hoje até as {r.now.time}</span>
        <Delta cur={r.orders.today} base={r.orders.yesterday} label="vs ontem" fmt={int} />
        <Delta cur={r.orders.today} base={r.orders.other} label={vsOther} fmt={int} />
      </div>
      <div className="kpi">
        <span className="k-label">Ticket médio</span>
        <span className="k-value">{r.ticket.today == null ? '—' : brl(r.ticket.today)}</span>
        <span className="k-note">hoje até as {r.now.time}</span>
        <Delta cur={r.ticket.today} base={r.ticket.yesterday} label="vs ontem" fmt={brl} />
        <Delta cur={r.ticket.today} base={r.ticket.other} label={vsOther} fmt={brl} />
      </div>
      <div className="kpi">
        <span className="k-label">Meta × Projeção {goalLabel}</span>
        <span className="k-value proj-number">{g.projection == null ? '—' : brl0(g.projection)}</span>
        <span className="k-note">
          {g.perStore ? 'Meta é do canal (todas as lojas): veja no painel geral'
            : g.goal == null ? <>Sem meta {goalLabel} · <Link to="/configuracoes/metas">Definir a meta</Link></>
            : <>Meta {goalLabel}: <b>{brl0(g.goal)}</b>{g.provisional ? ' (provisória)' : g.source === 'calculated' ? ' (sugerida)' : ''}</>}
        </span>
        {g.projection == null
          ? <span className="delta muted">Sem base para projetar (sem vendas nas últimas 4 semanas)</span>
          : <Delta cur={g.projection} base={g.goal} label="projeção vs meta" />}
      </div>
    </section>
  );
}

interface Col<T> { head: string; width?: number; num?: boolean; cell: (r: T) => ReactNode }

/** Tabela compacta: 5 linhas à vista, o resto rola dentro dela; Exportar baixa a lista completa. */
function ItemsTable<T extends { id: string }>({ title, summary, csv, cols, rows, total, empty }: {
  title: string; summary: ReactNode; csv: string; cols: Col<T>[]; rows: T[]; total: number; empty: string;
}) {
  return (
    <section className="panel stack-y" style={{ gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ margin: 0, flex: 1 }}>{title}</h2>
        <a className="btn sm" href={csv} aria-disabled={!total}><Download size={14} /> Exportar</a>
      </div>
      <div className="small">{summary}</div>
      {rows.length === 0 ? <div className="small muted">{empty}</div> : (
        <div className="scroll-5">
          <table className="grid" style={{ width: '100%', minWidth: 480 }}>
            <colgroup>{cols.map((c, i) => <col key={i} style={c.width ? { width: c.width } : undefined} />)}</colgroup>
            <thead><tr>{cols.map((c) => <th key={c.head} className={c.num ? 'num' : undefined}>{c.head}</th>)}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ cursor: 'default' }}>
                  {cols.map((c) => <td key={c.head} className={c.num ? 'num' : undefined}>{c.cell(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > rows.length && <span className="small muted">Mostrando {int(rows.length)} de {int(total)}. Exportar traz todos.</span>}
    </section>
  );
}

const skuCell = (r: { id: string; sku: string }) => <Link className="code" to={`/catalogo/${r.id}`}>{r.sku}</Link>;
const titleCell = (r: { title: string }) => <span className="ellipsis" title={r.title}>{r.title}</span>;
const stockCell = (r: { available: number }) => <span style={r.available <= 0 ? { color: 'var(--err)', fontWeight: 600 } : undefined}>{int(r.available)}</span>;
const listingCell = (r: { listing: string; listingStatus: string | null }) =>
  <span className={r.listingStatus === 'active' ? undefined : 'muted'}>{r.listing}</span>;

export function ChannelHome({ channel, notices, listings }: { channel: Channel; notices?: ReactNode; listings: ReactNode }) {
  const [period, setPeriod] = usePref<HomePeriod>('periodo', 'week', ['week', 'month']);
  const [healthOpen, setHealthOpen] = usePref<'open' | 'closed'>('saude', 'open', ['open', 'closed']);
  const [pendingOpen, setPendingOpen] = usePref<'open' | 'closed'>('pendencias', 'open', ['open', 'closed']);
  const { q: board, account } = useChannelBoard(channel);
  const scope = `channel=${channel}&period=${period}${account ? `&accountId=${account.id}` : ''}`;
  const results = useQuery({ queryKey: ['home', 'channel', 'results', scope], queryFn: () => api<Results>(`/home/channel/results?${scope}`),
    placeholderData: keepPreviousData, refetchInterval: 120_000 });
  const items = useQuery({ queryKey: ['home', 'channel', 'items', scope], queryFn: () => api<Items>(`/home/channel/items?${scope}`),
    placeholderData: keepPreviousData, refetchInterval: 300_000 });
  const acc = account ? `&accountId=${account.id}` : '';
  const [topDays, setTopDays] = usePref<'30' | '7'>('top', '30', ['30', '7']);
  const top = useQuery({ queryKey: ['home', 'top', channel, topDays, account?.id ?? null], queryFn: () => api<Top>(`/home/top?channel=${channel}&topDays=${topDays}${acc}`),
    placeholderData: keepPreviousData, refetchInterval: 300_000 });
  const r = results.data, it = items.data, d = board.data;
  const range = it ? `${PERIODS.find(([v]) => v === period)![1].toLowerCase()} atual desde ${dm(it.range.curFrom)} × ${PREV[period]} de ${dm(it.range.prevFrom)} a ${dm(it.range.prevTo)} (mesmo dia e hora)` : '';

  return (
    <div className="stack-y" style={{ gap: 'var(--gap)' }}>
      <div className="toolbar" style={{ margin: 0 }}>
        <div className="seg" role="group" aria-label="Período">
          {PERIODS.map(([v, l]) => <button key={v} type="button" aria-pressed={period === v} onClick={() => setPeriod(v)}>{l}</button>)}
        </div>
        {account && <span className="small muted">Loja: <b>{account.displayName ?? 'escolhida'}</b></span>}
      </div>

      {notices}
      {d && <CriticalAlerts channel={channel} d={d} />}
      {d && d.risk.count > 0 && (
        <div className="notice err small">
          <b>{int(d.risk.count)} pedido{d.risk.count === 1 ? '' : 's'} perto do prazo de envio</b>{d.risk.late ? ` (${int(d.risk.late)} já passaram do prazo)` : ''}, sem nota ou etiqueta.{' '}
          <Link to={`/pedidos?alert=at_risk&channel=${channel}`}>Ver pedidos</Link>
        </div>
      )}

      {results.error ? <ErrorBox error={results.error} /> : !r ? <Loading /> : (
        <div style={{ opacity: results.isPlaceholderData ? 0.55 : 1 }}><ResultCards r={r} period={period} /></div>
      )}

      <section aria-label="Anúncios" className="stack-y" style={{ gap: 8 }}>
        <h2 className="section-title">Anúncios</h2>
        {listings}
      </section>

      {board.error ? <ErrorBox error={board.error} /> : !d ? <Loading /> : (
        <div className="cols">
          <HealthPanel channel={channel} d={d} collapsed={healthOpen === 'closed'} onToggle={() => setHealthOpen(healthOpen === 'closed' ? 'open' : 'closed')} />
          <PendingPanel channel={channel} d={d} collapsed={pendingOpen === 'closed'} onToggle={() => setPendingOpen(pendingOpen === 'closed' ? 'open' : 'closed')} />
        </div>
      )}

      {r?.buybox && <BuyBoxNotice bb={r.buybox} />}

      {items.error ? <ErrorBox error={items.error} /> : !it ? <Loading /> : (
        <div className="cols" style={{ opacity: items.isPlaceholderData ? 0.55 : 1 }}>
          <ItemsTable title="Itens que pararam de vender" csv={`/api/v1/home/channel/stopped.csv?${scope}`} total={it.stopped.count} rows={it.stopped.items}
            summary={<><b className="tnum" style={it.stopped.count ? { color: 'var(--err)' } : undefined}>{int(it.stopped.count)}</b> ite{it.stopped.count === 1 ? 'm' : 'ns'} · vendeu {PREV_IN[period]} e nada {CUR_IN[period]} <span className="muted">({range})</span></>}
            empty={`Nenhum item parou de vender (${CUR[period]} × ${PREV[period]}).`}
            cols={[
              { head: 'SKU', width: 84, cell: skuCell },
              { head: 'Item', cell: titleCell },
              { head: `Vendas ${SHORT_PREV[period]}`, width: 74, num: true, cell: (x) => int(x.prevUnits) },
              { head: `Vendas ${SHORT_CUR[period]}`, width: 70, num: true, cell: (x) => int(x.curUnits) },
              { head: 'Estoque', width: 64, num: true, cell: stockCell },
              { head: 'Status do anúncio', width: 92, cell: listingCell },
            ]} />
          <ItemsTable title="Top itens em ruptura" csv={`/api/v1/home/channel/rupture.csv?${scope}`} total={it.rupture.count} rows={it.rupture.items}
            summary={<><b className="tnum" style={it.rupture.count ? { color: 'var(--err)' } : undefined}>{int(it.rupture.count)}</b> SKU{it.rupture.count === 1 ? '' : 's'} · {int(it.rupture.listings)} anúncio{it.rupture.listings === 1 ? '' : 's'} sem estoque ou pausado{it.rupture.listings === 1 ? '' : 's'} por falta de estoque <span className="muted">(ordem: vendas {PREV_IN[period]})</span></>}
            empty="Nenhum anúncio em ruptura."
            cols={[
              { head: 'SKU', width: 84, cell: skuCell },
              { head: 'Item', cell: titleCell },
              { head: `Vendas ${SHORT_PREV[period]}`, width: 74, num: true, cell: (x) => int(x.prevUnits) },
              { head: 'Dias em ruptura', width: 70, num: true, cell: (x) => (x.days == null
                ? <span className="muted" title="Nunca vendeu: sem data de referência">—</span>
                : <span title="Contado desde a última venda (não há histórico de estoque)">{int(x.days)}</span>) },
              { head: 'Anúncio', width: 104, cell: listingCell },
            ]} />
        </div>
      )}

      {top.error ? <ErrorBox error={top.error} /> : !top.data ? <Loading /> : (
        <TopPanel top={top.data} faded={top.isPlaceholderData} onDays={(v) => setTopDays(v === 7 ? '7' : '30')}
          csv={`/api/v1/home/top.csv?channel=${channel}&topDays=${topDays}${acc}`} />
      )}
    </div>
  );
}
